/**
 * Scenario Status Component
 *
 * Displays multi-step progress indicator for demo scenario execution.
 */

import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export interface ScenarioStatusProps {
  steps: string[];
  currentStep: string;
  isComplete: boolean;
  isError: boolean;
  errorMessage?: string;
}

export function ScenarioStatus({
  steps,
  currentStep,
  isComplete,
  isError,
  errorMessage,
}: ScenarioStatusProps) {
  const getCurrentStepIndex = () => {
    return steps.findIndex((step) => step === currentStep);
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <Box sx={{ py: 2 }}>
      <Stack spacing={1.5}>
        {steps.map((step, index) => {
          const isPast = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;
          const isFuture = index > currentStepIndex;

          let icon;
          let color: 'default' | 'success' | 'error' | 'primary' = 'default';

          if (isPast || (isComplete && !isError)) {
            icon = <CheckCircleIcon sx={{ fontSize: 20 }} />;
            color = 'success';
          } else if (isCurrent && isError) {
            icon = <ErrorIcon sx={{ fontSize: 20 }} />;
            color = 'error';
          } else if (isCurrent) {
            icon = <CircularProgress size={16} />;
            color = 'primary';
          }

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                opacity: isFuture ? 0.4 : 1,
              }}
            >
              <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                {icon}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontWeight: isCurrent ? 600 : 400,
                  color: isError && isCurrent ? 'error.main' : 'text.primary',
                }}
              >
                {step}
              </Typography>
              {isPast && !isError && (
                <Chip
                  label="✓"
                  size="small"
                  color="success"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Box>
          );
        })}
      </Stack>

      {isComplete && !isError && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: 'success.main' }}
          >
            ✓ Scenario Complete!
          </Typography>
        </Box>
      )}

      {isError && errorMessage && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            bgcolor: 'error.lighter',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'error.light',
          }}
        >
          <Typography variant="body2" color="error.main">
            <strong>Error:</strong> {errorMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
